import {useEffect, useRef, useState, useCallback, useContext} from 'react';
import classNames from 'classnames';
import {Popper, PopperTrigger} from '@baidu/luffy-comp-popper';

import sdk from '@/sdk';
import {MoreSvg, TagSvg} from '@/icons';
import Icon from '@/components/Icon';
import TagRecommendGuide from '@/components/guide/TagRecommendGuide';
import {User} from '@/interface';

import TagItem from '../TagItem';
import TagAdd from '../TagAdd';
import {calcFoldIndex} from '../utils';
import {TagData} from '../interface';
import {leftElResizeContext} from '../../context';

import styles from './index.module.scss';

const MAX_TAG_NAME_LENGTH = 20;
const MAX_TAG_NUM = 10;

interface IProps {
    className?: string;
    tags: TagData[];
    docGuid?: string;
    editable: boolean;
    maxTags?: number;
    maxTagName?: number;
    onAdd?: (tagName: TagData['name']) => Promise<string | null | void>;
    onDelete?: (tag: TagData) => Promise<string | null | void>;
    rootId?: string;
}

const Tag: React.FC<IProps> = ({
    className,
    tags,
    editable,
    maxTags = MAX_TAG_NUM,
    docGuid = '',
    maxTagName = MAX_TAG_NAME_LENGTH,
    onAdd,
    onDelete,
    rootId,
}) => {
    const addTagRef = useRef<HTMLDivElement | null>(null);
    const tagRef = useRef<HTMLDivElement[]>([]);
    const tagShadowRef = useRef<HTMLDivElement[]>([]);
    const tagContainerRef = useRef<HTMLDivElement | null>(null);

    function stopPropagation(e: React.MouseEvent) {
        e.stopPropagation();
    }

    const leftElResizeFlag = useContext(leftElResizeContext);
    const [foldIndex, setFoldIndex] = useState(-1);
    const updateFold = useCallback(() => {
        if (tagContainerRef.current) {
            const index = calcFoldIndex(tagShadowRef.current, tagContainerRef.current, addTagRef.current);
            setFoldIndex(index);
        }
    }, []);
    useEffect(() => {
        updateFold();
    }, [updateFold, tags, leftElResizeFlag]);

    const [delDialogVisible, setDelDialogVisible] = useState(true);
    const [delDialogTargetEl, setDelDialogTargetEl] = useState<HTMLElement | null>(null);
    const currentDelTagRef = useRef<TagData | null>(null);
    function handleDelete() {
        if (onDelete && currentDelTagRef.current) {
            onDelete(currentDelTagRef.current);
        }
    }
    const onClickDelete = useCallback((tag: TagData, index: number) => {
        setDelDialogTargetEl(tagRef.current[index]);
        currentDelTagRef.current = tag;
        setDelDialogVisible(true);
    }, []);
    const closeDelDialog = useCallback(() => {
        setDelDialogVisible(false);
    }, []);
    useEffect(() => {
        document.addEventListener('click', closeDelDialog);

        return () => {
            document.removeEventListener('click', closeDelDialog);
        };
    }, [closeDelDialog]);

    async function handleAdd(tagName: TagData['name']) {
        if (tags.length >= maxTags) {
            return;
        }
        try {
            onAdd && (await onAdd(tagName));
        } catch (err) {
            console.error(err);
        }
    }

    const foldedTags = foldIndex >= 0 ? tags.slice(foldIndex) : [];
    const unFoldedTags = foldIndex >= 0 ? tags.slice(0, foldIndex) : tags;

    // 第一个推荐标签
    const targetItem = unFoldedTags.find(item => {
        return item.tagType === 1;
    });

    const getGuideOfTagRecommendIsRead = useCallback(() => {
        const user = sdk.as.user as User;
        const {
            settings: {newGuide = {}},
        } = user;
        return !!newGuide.tagRecommendIsRead;
    }, []);

    const renderTagList = useCallback(
        (tags: TagData[], lastIndex: number = 0) => {
            return tags.map((tag, index) => {
                const isTagRecommendGuide = targetItem?.name === tag.name;
                const globalIndex = lastIndex + index;

                const onClickDeleteWrapper = (tag: TagData) => {
                    onClickDelete(tag, globalIndex);
                };

                const tagNode = (
                    <TagItem
                        ref={ref => {
                            ref && (tagRef.current[globalIndex] = ref);
                        }}
                        docGuid={docGuid}
                        key={tag.name}
                        {...tag}
                        disabled={!editable}
                        onClickClose={onClickDeleteWrapper}
                        rootId={rootId}
                    />
                );
                return isTagRecommendGuide ? (
                    <TagRecommendGuide
                        key={tag.name}
                        getIsRead={getGuideOfTagRecommendIsRead}
                        isTarget={isTagRecommendGuide}
                        target={tagRef.current[globalIndex]}
                    >
                        {tagNode}
                    </TagRecommendGuide>
                ) : (
                    tagNode
                );
            });
        },
        [docGuid, editable, getGuideOfTagRecommendIsRead, onClickDelete, targetItem?.name, rootId]
    );

    function renderMore() {
        if (foldedTags.length <= 0) {
            return null;
        }
        return (
            <PopperTrigger>
                <Icon className={styles.moreBtn} svg={MoreSvg} />
                <Popper
                    // 加key是为了在删除标签后，Popper浮窗能自动更新位置
                    key={foldedTags.length}
                    placement={'top-start'}
                    className={styles.popper}
                    modifiers={{
                        offset: {
                            offset: '0,4',
                        },
                    }}
                >
                    <div className={styles.foldList}>
                        {foldedTags.map((tag, index) => {
                            const globalIndex = unFoldedTags.length + index;

                            const onClickDeleteWrapper = (tag: TagData) => {
                                onClickDelete(tag, globalIndex);
                            };

                            return (
                                <div className={styles.tagBlock} key={tag.name}>
                                    <TagItem
                                        ref={ref => {
                                            ref && (tagRef.current[globalIndex] = ref);
                                        }}
                                        docGuid={docGuid}
                                        {...tag}
                                        disabled={!editable}
                                        onClickClose={onClickDeleteWrapper}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </Popper>
            </PopperTrigger>
        );
    }

    const showNoTagTips = !editable && tags.length === 0;

    return (
        <div className={classNames(styles.wrapper, className)}>
            <div className={styles.tagList} ref={tagContainerRef}>
                {showNoTagTips && (
                    <div className={styles.noTagTips}>
                        <Icon className={styles.icon} svg={TagSvg} />
                        <span>无标签</span>
                    </div>
                )}
                {/* shadowTags 用于计算出需要折叠的部分，不显示在页面上 */}
                {tags.length > 0 && (
                    <div className={styles.shadowTags}>
                        {tags.map((tag, index) => {
                            return (
                                <TagItem
                                    ref={ref => {
                                        ref && (tagShadowRef.current[index] = ref);
                                    }}
                                    key={tag.name}
                                    {...tag}
                                    disabled={!editable}
                                />
                            );
                        })}
                    </div>
                )}

                {renderTagList(unFoldedTags)}
                {renderMore()}

                {editable ? (
                    <TagAdd ref={addTagRef} maxTagName={maxTagName} maxTags={maxTags} tags={tags} onAdd={handleAdd} />
                ) : null}
            </div>

            <Popper
                // 加key是为了在删除标签后，Popper浮窗重建盖住更多标签的浮窗
                key={foldedTags.length}
                className={styles.delDialog}
                placement="top-start"
                modifiers={{
                    offset: {
                        offset: '0,4',
                    },
                }}
                visible={delDialogVisible}
                target={delDialogTargetEl}
                preventOverflowBoundary={'viewport'}
            >
                <div className={styles.del} onClick={stopPropagation} onMouseDown={stopPropagation}>
                    <div className={styles.title}>确定删除标签吗？</div>
                    <div className={styles.btns}>
                        <div className={styles.cancel} onClick={closeDelDialog}>
                            取消
                        </div>
                        <div className={styles.confirm} onClick={handleDelete}>
                            确定
                        </div>
                    </div>
                </div>
            </Popper>
        </div>
    );
};

export default Tag;
